import { z } from 'zod';
import { baseListQuerySchema, sortOrderSchema, } from './helpers';
import { ColumnDataTypes, createTableQuerySchema, FilterOperators, FiltersSchema, FilterValueSchema, TableResponseMetaSchema, } from './table';
import { filterOptionItemSchema } from './mcp';
import { ENDPOINT_FILTER_FIELD, ENDPOINT_FILTER_OPERATORS, endpointRowSummaryShape, } from './endpoints';
import { loggedInUserCountsSchema } from './shadow-ai-logged-in-user';
import { knownTotalQuerySchema } from './known-total';
import { toolApprovalQueryParamSchema, toolApprovalSchema, } from './shadow-ai-tool-approvals';
// ============================================================================
// Filterable Fields
// ============================================================================
export const BROWSER_AI_TOOLS_FILTERABLE_FIELDS = {
    DOMAIN: 'domain',
    NAME: 'name',
    CATEGORY: 'category',
    TREND: 'trend',
    USERS: 'users',
    FIRST_DETECTED: 'firstDetected',
    LAST_SEEN: 'lastSeen',
    // KIR-2916: filter tools by their users' logged-in account email.
    LOGGED_IN_USER: 'loggedInUser',
    // KIR-5098: tools seen on an endpoint matching this name or ID.
    ENDPOINT: ENDPOINT_FILTER_FIELD,
};
// ============================================================================
// Sortable Fields
// ============================================================================
export const BROWSER_AI_TOOLS_SORTABLE_FIELDS = {
    DOMAIN: 'domain',
    NAME: 'name',
    CATEGORY: 'category',
    TREND: 'trend',
    USERS: 'users',
    FIRST_DETECTED: 'firstDetected',
    LAST_SEEN: 'lastSeen',
    // KIR-3429: lexicographic sort on the (personal, corporate, matched) count
    // tuple. Computed in the handler (not SQL), see list-browser-ai-tools.ts.
    LOGGED_IN_USER: 'loggedInUser',
    // KIR-5098: number of distinct endpoints.
    ENDPOINT: ENDPOINT_FILTER_FIELD,
};
export const browserAiToolsSortFieldSchema = z.enum([
    BROWSER_AI_TOOLS_SORTABLE_FIELDS.DOMAIN,
    BROWSER_AI_TOOLS_SORTABLE_FIELDS.NAME,
    BROWSER_AI_TOOLS_SORTABLE_FIELDS.CATEGORY,
    BROWSER_AI_TOOLS_SORTABLE_FIELDS.TREND,
    BROWSER_AI_TOOLS_SORTABLE_FIELDS.USERS,
    BROWSER_AI_TOOLS_SORTABLE_FIELDS.FIRST_DETECTED,
    BROWSER_AI_TOOLS_SORTABLE_FIELDS.LAST_SEEN,
    BROWSER_AI_TOOLS_SORTABLE_FIELDS.LOGGED_IN_USER,
    BROWSER_AI_TOOLS_SORTABLE_FIELDS.ENDPOINT,
]);
// ============================================================================
// Searchable Fields
// ============================================================================
export const BROWSER_AI_TOOLS_SEARCHABLE_FIELDS = {
    DOMAIN: 'domain',
    NAME: 'name',
    CATEGORY: 'category',
    TREND: 'trend',
    USERS: 'users',
    // KIR-5098: endpoint name or ID.
    ENDPOINT: ENDPOINT_FILTER_FIELD,
};
// ============================================================================
// Filter Option Fields (dynamic endpoints only)
// ============================================================================
/**
 * Fields that have a backend /filter-options endpoint.
 * risk/trend use static options; firstDetected/lastSeen use date pickers.
 */
export const BROWSER_AI_TOOLS_FILTER_OPTION_FIELDS = {
    DOMAIN: 'domain',
    NAME: 'name',
    CATEGORY: 'category',
    USERS: 'users',
    // KIR-3429: distinct logged-in account values for the multiselect filter.
    LOGGED_IN_USER: 'loggedInUser',
    // KIR-5098: endpoint suggestions for the Matches list.
    ENDPOINT: ENDPOINT_FILTER_FIELD,
};
export const browserAiToolsFilterOptionFieldSchema = z.enum([
    BROWSER_AI_TOOLS_FILTER_OPTION_FIELDS.DOMAIN,
    BROWSER_AI_TOOLS_FILTER_OPTION_FIELDS.NAME,
    BROWSER_AI_TOOLS_FILTER_OPTION_FIELDS.CATEGORY,
    BROWSER_AI_TOOLS_FILTER_OPTION_FIELDS.USERS,
    BROWSER_AI_TOOLS_FILTER_OPTION_FIELDS.LOGGED_IN_USER,
    BROWSER_AI_TOOLS_FILTER_OPTION_FIELDS.ENDPOINT,
]);
// ============================================================================
// Trend Category
// ============================================================================
export const browserAiToolsTrendCategorySchema = z.enum([
    'new',
    'resumed',
    'increasing',
    'stable',
    'decreasing',
    'inactive',
]);
// ============================================================================
// Navigation Type ("how the domain was reached") — KIR-3890
// ============================================================================
/** Coarse navigation buckets for the "how it was reached" filter. Mirrors the agent's navType* constants. */
export const BROWSER_AI_NAVIGATION_TYPES = {
    REAL: 'real',
    REDIRECT: 'redirect',
    ADDRESS_BAR: 'address_bar',
    ADS_TRACKING: 'ads_tracking',
};
export const browserAiNavigationTypeSchema = z.enum([
    BROWSER_AI_NAVIGATION_TYPES.REAL,
    BROWSER_AI_NAVIGATION_TYPES.REDIRECT,
    BROWSER_AI_NAVIGATION_TYPES.ADDRESS_BAR,
    BROWSER_AI_NAVIGATION_TYPES.ADS_TRACKING,
]);
/** Human labels for the dropdown + evidence panel. */
export const BROWSER_AI_NAVIGATION_TYPE_LABELS = {
    real: 'Real (typed / clicked)',
    redirect: 'Redirects',
    address_bar: 'Address-bar suggestions',
    ads_tracking: 'Ads, tracking & query strings',
};
/** Maps each bucket to its per-bucket count field; the filter matches rows where that count > 0. */
export const BROWSER_AI_NAVIGATION_TYPE_COUNT_KEYS = {
    real: 'realCount',
    redirect: 'redirectCount',
    address_bar: 'addressBarCount',
    ads_tracking: 'adsTrackingCount',
};
/**
 * Legacy `navigationType` values per bucket, to filter rows from agents predating
 * per-bucket counts (old dominant `typed|link|redirect|other` + the 4-bucket value).
 */
export const BROWSER_AI_NAVIGATION_TYPE_LEGACY_VALUES = {
    real: ['real', 'typed', 'link', 'other'],
    redirect: ['redirect'],
    address_bar: ['address_bar'],
    ads_tracking: ['ads_tracking'],
};
// ============================================================================
// Response Item
// ============================================================================
export const browserAiToolListItemSchema = z.object({
    domain: z.string(),
    name: z.string(),
    category: z.string(),
    trend: browserAiToolsTrendCategorySchema.nullable(),
    userCount: z.number().int().min(0),
    firstDetected: z.string().datetime({ offset: true }).nullable(),
    lastSeen: z.string().datetime({ offset: true }).nullable(),
    // KIR-3890: "why this domain was flagged" evidence. visitCount = total matched
    // visits; realVisitCount excludes iframe/subframe loads (both default to 0 on
    // rows predating the agent change). navigationType = dominant bucket
    // (real | redirect | address_bar | ads_tracking); sampleMatchedUrl = one
    // representative URL — both null on those legacy rows.
    visitCount: z.number().int().min(0),
    realVisitCount: z.number().int().min(0),
    navigationType: z.string().nullable(),
    sampleMatchedUrl: z.string().nullable(),
    // KIR-3890: per-bucket visit counts (summed) driving the has-≥1-of-type filter
    // + drill-down breakdown. Default/legacy rows report 0.
    realCount: z.number().int().min(0).optional(),
    redirectCount: z.number().int().min(0).optional(),
    addressBarCount: z.number().int().min(0).optional(),
    adsTrackingCount: z.number().int().min(0).optional(),
    // KIR-3495: raw per-user evidence behind the row, returned ONLY when the
    // request narrows to a single member (`scopeUserId`). Feeds the user-scope
    // page's browser-history detail dialog.
    extras: z.record(z.string(), z.string()).nullable().optional(),
    // KIR-2916: present only when the logged-in-user feature is enabled.
    loggedInUserCounts: loggedInUserCountsSchema.optional(),
    // KIR-4854: org-wide approval state. null = not approved; absent when the
    // tool-approval feature flag is off.
    approval: toolApprovalSchema.nullable().optional(),
    // KIR-5098: endpoint count + the endpoints matching an endpoint filter.
    ...endpointRowSummaryShape,
});
// ============================================================================
// Filter Column Data Types (used by the query schema; exported so the personal
// browser-tools contract can derive its filter-column map by omitting the users key)
// ============================================================================
export const BROWSER_AI_TOOLS_FILTER_COLUMN_DATA_TYPES = {
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.DOMAIN]: ColumnDataTypes.STRING,
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.NAME]: ColumnDataTypes.STRING,
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.CATEGORY]: ColumnDataTypes.ENUM,
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.TREND]: ColumnDataTypes.ENUM,
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.USERS]: ColumnDataTypes.ENUM,
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED]: ColumnDataTypes.DATE,
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.LAST_SEEN]: ColumnDataTypes.DATE,
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.LOGGED_IN_USER]: ColumnDataTypes.STRING,
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.ENDPOINT]: ColumnDataTypes.STRING,
};
// Both date columns drop `equals`/`notEquals`, keeping the range operators plus
// the value-less operators.
export const BROWSER_AI_TOOLS_OPERATOR_OVERRIDES = {
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.ENDPOINT]: ENDPOINT_FILTER_OPERATORS,
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.LAST_SEEN]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
};
// ============================================================================
// Filter Options Schemas
// ============================================================================
export const browserAiToolsFilterOptionsQuerySchema = z.object({
    field: browserAiToolsFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
    // KIR-3495: scope the options to a single org member (Shadow AI user-scope
    // page). Honoured only for manager/owner/support — everyone else is already
    // pinned to their own rows, so this can never widen what a caller may see.
    scopeUserId: z.string().uuid().optional(),
});
export const browserAiToolsFilterOptionsResponseSchema = z.object({
    field: browserAiToolsFilterOptionFieldSchema,
    options: z.union([z.array(z.string()), z.array(filterOptionItemSchema)]),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
const browserAiToolsTableQueryBaseSchema = baseListQuerySchema.extend({
    sortBy: browserAiToolsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalQuerySchema,
    navigationType: browserAiNavigationTypeSchema.optional(),
    minVisits: z.coerce.number().int().min(1).optional(),
    // KIR-3495: restrict the whole result set to one org member so the Shadow AI
    // user-scope page reuses this table with per-user aggregates (userCount,
    // lastSeen). Privileged roles only — see the table engine.
    scopeUserId: z.string().uuid().optional(),
    // KIR-4854: Show filter (not_approved | approved | all). Omitted = all.
    approval: toolApprovalQueryParamSchema,
});
export const browserAiToolsTableQuerySchema = createTableQuerySchema(browserAiToolsTableQueryBaseSchema, BROWSER_AI_TOOLS_FILTER_COLUMN_DATA_TYPES, BROWSER_AI_TOOLS_OPERATOR_OVERRIDES).superRefine((data, ctx) => {
    data.filters.forEach((filter, index) => {
        if (filter.field === BROWSER_AI_TOOLS_FILTERABLE_FIELDS.USERS) {
            filter.value.forEach((v, valueIndex) => {
                if (!z.string().uuid().safeParse(v).success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Filter values for users field must be valid UUIDs',
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
        if (filter.field === BROWSER_AI_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED ||
            filter.field === BROWSER_AI_TOOLS_FILTERABLE_FIELDS.LAST_SEEN) {
            filter.value.forEach((v, valueIndex) => {
                if (!z.string().datetime({ offset: true, local: true }).safeParse(v)
                    .success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Filter values for ${filter.field} field must be valid ISO 8601 datetime strings`,
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
        if (filter.field === BROWSER_AI_TOOLS_FILTERABLE_FIELDS.TREND) {
            filter.value.forEach((v, valueIndex) => {
                if (!browserAiToolsTrendCategorySchema.safeParse(v).success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Filter values for trend field must be one of: ${browserAiToolsTrendCategorySchema.options.join(', ')}`,
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
    });
});
// ============================================================================
// API Client Params Schema (for serializing DataTable query params)
// ============================================================================
export const listBrowserAiToolsDataTableParamsSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    sortBy: browserAiToolsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: z.array(FilterValueSchema).optional(),
    knownTotal: knownTotalQuerySchema,
    // KIR-3890 evidence filters (see BrowserAiToolsTableQuery).
    navigationType: browserAiNavigationTypeSchema.optional(),
    minVisits: z.coerce.number().int().min(1).optional(),
    scopeUserId: z.string().uuid().optional(),
    // KIR-4854: Show filter (not_approved | approved | all). Omitted = all.
    approval: toolApprovalQueryParamSchema,
});
// ============================================================================
// Table Response Schema
// ============================================================================
export const browserAiToolsTableResponseSchema = z.object({
    data: z.array(browserAiToolListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
// ============================================================================
// Static Filter Option Constants (for frontend use)
// ============================================================================
export const BROWSER_AI_TOOLS_TREND_OPTIONS = [
    { value: 'new', label: 'New' },
    { value: 'resumed', label: 'Resumed' },
    { value: 'increasing', label: 'Increasing' },
    { value: 'stable', label: 'Stable' },
    { value: 'decreasing', label: 'Decreasing' },
    { value: 'inactive', label: 'Inactive' },
];
//# sourceMappingURL=shadow-ai-browser-tools.js.map